import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bPaymentReciptApi, {
  PaymentReceiptBackendData
} from 'api/b2b/PaymentRecipt'
import B2bTDPAPI from 'api/b2b/TdpApi'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

interface Company {
  tenant_id: string
  company_name: string
  id: string
  currency: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
  currency: string
}

interface FormData {
  companyName: CompanyOption | null
  companyId: string
  paymentCurrency: string
  paymentAmount: string
  roe: string
  receiptCurrency: string
  receiptAmount: string
  receiptDate: string
  particulars: string
  customerModeOfPayment: string
  depositRefNo: string
  remarks: string
}

export default function AddEditPaymentReceipt() {
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(companySearchTerm, 500) // 500ms debounce

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormData>({
    defaultValues: {
      companyName: null,
      companyId: '',
      paymentCurrency: '',
      paymentAmount: '',
      roe: '',
      receiptCurrency: '',
      receiptAmount: '',
      receiptDate: '',
      particulars: '',
      customerModeOfPayment: '',
      depositRefNo: '',
      remarks: ''
    }
  })

  const navigate = useNavigate()

  // Watch form values for calculations
  const paymentAmount = watch('paymentAmount')
  const roe = watch('roe')
  const companyName = watch('companyName')

  // Debounced company search query
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError
    // error: tenantError
  } = useQuery<TenantData>(
    ['tenant-search', debouncedSearchTerm],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        company_name: debouncedSearchTerm
      })
      return data
    },
    {
      enabled: debouncedSearchTerm.length >= 2, // Only search when user types at least 2 characters
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Failed to fetch companies:', error)
      }
    }
  )

  const { mutate: addPaymentRecipt } = useMutation({
    mutationFn: async (payload: PaymentReceiptBackendData) => {
      const token = OrbitSession.get('faberToken')
      return B2bPaymentReciptApi.addPaymentReceipt(token, payload)
    },
    onSuccess: () => {
      toast.success('Wallet adjustment added successfully')
      navigate('/hq/b2b/payments')
    },
    onError: () => {
      toast.error('Failed to add wallet adjustment')
    }
  })

  // Memoized company options
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || '',
      currency: company.currency || ''
    }))
  }, [tenantData])

  const handleCompanyInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCompanySearchTerm(value)
    },
    []
  )

  // Auto-fill fields when company is selected
  useEffect(() => {
    if (companyName?.companyId) {
      setValue('companyId', companyName.companyId)
      setValue('paymentCurrency', companyName.currency)
      setValue('receiptCurrency', companyName.currency)
    } else {
      setValue('companyId', '')
    }
  }, [companyName, setValue])

  // Auto-calculate receipt amount when payment amount, ROE, or currency changes
  useEffect(() => {
    if (paymentAmount && roe) {
      const calculatedAmount = (
        parseFloat(paymentAmount) * parseFloat(roe)
      ).toFixed(2)
      setValue('receiptAmount', calculatedAmount)
    } else {
      setValue('receiptAmount', '')
    }
  }, [paymentAmount, roe, setValue])
  const currencyList = ['USD', 'EUR', 'INR']
  const paymentModes = ['Cash', 'Bank Transfer', 'Credit Card']

  const onSubmit = (data: FormData) => {
    const payload: PaymentReceiptBackendData = {
      tenant_id: data.companyName?.companyId || '',
      payment_currency: data.paymentCurrency,
      mode_of_payment: data.customerModeOfPayment,
      payment_amount: data.paymentAmount,
      roe: data.roe,
      receipt_currency: data.receiptCurrency,
      receipt_amount: data.receiptAmount,
      receipt_date: new Date().toISOString(),
      deposit_ref_no: data.depositRefNo,
      remarks: data.remarks,
      created_by: OrbitSession.get('email') || '',
      is_active: true,
      receipt_particulars: data.particulars
    }
    addPaymentRecipt(payload)
  }

  const handleReset = () => {
    reset()
    setCompanySearchTerm('')
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h6' gutterBottom>
        Add / Edit Payment Receipt
      </Typography>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2
        }}
      >
        {/* Company Name (Dynamic Search) */}
        <Controller
          name='companyName'
          control={control}
          rules={{ required: 'Company name is required' }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              options={companyOptions}
              loading={isTenantLoading}
              getOptionLabel={(option: CompanyOption | null) =>
                option?.label || ''
              }
              isOptionEqualToValue={(
                option: CompanyOption,
                value: CompanyOption | null
              ) => option?.value === value?.value}
              onChange={(_, data: CompanyOption | null) => field.onChange(data)}
              onInputChange={handleCompanyInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Company Name'
                  required
                  error={!!error}
                  helperText={
                    error?.message ||
                    (debouncedSearchTerm.length > 0 &&
                    debouncedSearchTerm.length < 2
                      ? 'Type at least 2 characters to search'
                      : '')
                  }
                  placeholder='Type company name to search...'
                  InputProps={{
                    ...params.InputProps
                  }}
                />
              )}
              noOptionsText={
                debouncedSearchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : isTenantLoading
                  ? 'Searching...'
                  : tenantFetchError
                  ? 'Error loading companies'
                  : 'No companies found'
              }
            />
          )}
        />

        {/* Company ID (Read Only - Auto-filled) */}
        <Controller
          name='companyId'
          control={control}
          disabled
          render={({ field }) => (
            <TextField
              {...field}
              label='Company ID'
              InputProps={{ readOnly: true }}
              required
              placeholder='Auto-filled when company is selected'
            />
          )}
        />

        {/* Payment Currency */}
        <Controller
          name='paymentCurrency'
          control={control}
          rules={{ required: 'Payment currency is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              select
              label='Payment Currency'
              required
              error={!!error}
              helperText={error?.message}
            >
              <MenuItem value=''>Select Currency</MenuItem>
              {currencyList.map((cur) => (
                <MenuItem key={cur} value={cur}>
                  {cur}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* Payment Amount */}
        <Controller
          name='paymentAmount'
          control={control}
          rules={{ required: 'Payment amount is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label='Payment Amount '
              type='number'
              inputProps={{ step: '0.01' }}
              required
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        {/* ROE */}
        <Controller
          name='roe'
          control={control}
          rules={{ required: 'ROE is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label='ROE (Rate of Exchange) '
              type='number'
              inputProps={{ step: '0.01' }}
              required
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        {/* Receipt Currency (Read Only - Auto-calculated) */}
        <Controller
          name='receiptCurrency'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label='Receipt Currency'
              InputProps={{ readOnly: true }}
              placeholder='Auto-filled based on payment currency'
            />
          )}
        />

        {/* Receipt Amount (Read Only - Auto-calculated) */}
        <Controller
          name='receiptAmount'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label='Receipt Amount'
              InputProps={{ readOnly: true }}
              placeholder='Auto-calculated (Payment Amount × ROE)'
            />
          )}
        />

        {/* Receipt Date */}
        <Controller
          name='receiptDate'
          control={control}
          rules={{ required: 'Receipt date is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='date'
              label='Receipt Date '
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              required
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        {/* Particulars */}
        <Controller
          name='particulars'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label='Particulars'
              multiline
              rows={3}
              sx={{ gridColumn: 'span 2' }}
            />
          )}
        />

        {/* Customer Mode of Payment */}
        <Controller
          name='customerModeOfPayment'
          control={control}
          rules={{ required: 'Payment mode is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              select
              label='Customer Mode of Payment '
              required
              error={!!error}
              helperText={error?.message}
            >
              <MenuItem value=''>Select Payment Mode</MenuItem>
              {paymentModes.map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {mode}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* Deposit / Transfer Ref No */}
        <Controller
          name='depositRefNo'
          control={control}
          rules={{ required: 'Reference number is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label='Deposit / Transfer Ref No '
              required
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        {/* Remarks */}
        <Controller
          name='remarks'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label='Remarks'
              multiline
              rows={2}
              sx={{ gridColumn: 'span 2' }}
            />
          )}
        />

        {/* Error display for company search */}
        {tenantFetchError && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant='body2' color='error'>
              Error loading companies
            </Typography>
          </Box>
        )}

        {/* Buttons */}
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 2 }}>
          <Button variant='outlined' type='button' onClick={handleReset}>
            Reset
          </Button>
          <Button variant='contained' color='primary' type='submit'>
            Save
          </Button>
          <Button
            variant='outlined'
            type='button'
            onClick={() => navigate('/hq/b2b/payments')}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
